// Watches panel for one persistent-agent thread (canonical scope).
// Prefer this over group watches for missions, agent set_watch, and benchmarks. — list / pause / resume /
// delete the thread's MCP watches and attach new ones through an inline
// wizard. A watch is a host-side poller: it invokes an MCP tool every N
// seconds and, when the (optionally selector-projected) response changes,
// writes the result into a fact lane and wakes the agent — so the thread
// sees fresh tool data without burning a turn to query for it. The wizard
// previews the tool's output live (two back-to-back polls), highlights
// fields that churn between them, and builds the selector by clicking
// nodes in the response tree. An "advanced" section swaps hash-on-change
// detection for a sandboxed JS transform that emits per-subject events.
import { useClient } from '@cortex/react-query';
import type {
  GenerateWatchSetupResult,
  PersistentAgentWatch,
  PersistentAgentWatchDelivery,
  PreviewPersistentAgentWatchResult,
  WatchQuery,
} from '@cortex/types';
import { compileWatchQuery, parseCompiledWatchQuery, parseWatchSelector } from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import { formatDateTime, formatRelative } from '../../lib/format';

import { parseJsonObjectText, parseTimeoutMs } from './json-object-field';
import {
  PreviewExplorer,
  ToolField,
  TransformEditor,
  TransformResultPanel,
  emptyTransformFields,
  useWatchConnections,
  watchTransformTemplates,
  type ITransformFieldsValue,
} from './watch-wizard-shared';

import './watches-panel.css';

interface IWatchForm {
  name: string;
  mcpConnectionId: string;
  toolName: string;
  argsText: string;
  intervalSec: string;
  laneId: string;
  /** Once the user edits the lane by hand, stop deriving it from the watch name. */
  laneTouched: boolean;
  /** Once the user edits the name, stop deriving it from the tool. */
  nameTouched: boolean;
  wakePrompt: string;
  wakeDelivery: PersistentAgentWatchDelivery;
  selector: string;
  /** Auto-expire after N seconds (empty = no expiry). */
  forSeconds: string;
  /** Declarative gate (hash mode). Empty op = off. */
  conditionOp: '' | 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'neq' | 'matches';
  conditionValue: string;
  conditionPath: string;
  conditionRising: boolean;
  /**
   * Declarative item filter (compiles to map JS on save).
   * When enabled, freeform map code is ignored on put.
   */
  queryEnabled: boolean;
  queryItems: string;
  queryWherePath: string;
  queryWhereOp: 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'neq';
  queryWhereValue: string;
  /** Comma-separated fields to keep on each match. */
  querySelect: string;
  queryWhen: 'changed' | 'nonempty';
  /** Transform (advanced) — empty code = hash-on-change detection. */
  transform: ITransformFieldsValue;
}

type DetectionPreset = 'any-change' | 'filter-items' | 'custom';

const emptyForm: IWatchForm = {
  name: '',
  mcpConnectionId: '',
  toolName: '',
  argsText: '{}',
  intervalSec: '5',
  laneId: '',
  laneTouched: false,
  nameTouched: false,
  wakePrompt: '',
  wakeDelivery: 'when-idle',
  selector: '',
  forSeconds: '',
  conditionOp: '',
  conditionValue: '',
  conditionPath: '',
  conditionRising: false, // every change while past gate
  queryEnabled: false,
  queryItems: '',
  queryWherePath: '',
  queryWhereOp: 'lt',
  queryWhereValue: '30',
  querySelect: '',
  queryWhen: 'changed',
  transform: emptyTransformFields,
};

interface IWatchRecipe {
  id: string;
  label: string;
  blurb: string;
  /** Prefill form fields (partial). */
  apply: () => Partial<IWatchForm> & { detection?: DetectionPreset; openAdvanced?: boolean };
}

const watchRecipes: IWatchRecipe[] = [
  {
    id: 'clear-until-zero',
    label: 'Work queue / clear until 0',
    blurb:
      'Board of open items — wake when the list changes. Default when-idle (no interrupt while busy).',
    apply: () => {
      return {
        name: 'open-work',
        laneId: 'open_work',
        laneTouched: true,
        intervalSec: '5',
        forSeconds: '300',
        wakeDelivery: 'when-idle',
        wakePrompt:
          'Open work updated and count is above zero. Read the open_work fact lane and clear until 0.',
        selector: 'events',
        conditionOp: 'gt',
        conditionValue: '0',
        conditionPath: '',
        conditionRising: false,
        queryEnabled: false,
        transform: emptyTransformFields,
        detection: 'any-change',
        openAdvanced: false,
      };
    },
  },
  {
    id: 'battery-low',
    label: 'Battery / threshold low',
    blurb: 'Filter drones where battery < 30; keep id + battery. No JS — uses item query.',
    apply: () => ({
      name: 'battery-low',
      laneId: 'low_battery',
      laneTouched: true,
      intervalSec: '10',
      forSeconds: '',
      wakeDelivery: 'when-idle',
      wakePrompt: 'Drones below battery floor. Check low_battery lane and act (RTH / swap).',
      selector: '',
      conditionOp: '' as const,
      conditionValue: '',
      conditionPath: '',
      queryEnabled: true,
      queryItems: 'drones',
      queryWherePath: 'battery',
      queryWhereOp: 'lt' as const,
      queryWhereValue: '30',
      querySelect: 'drone_id, battery',
      queryWhen: 'changed' as const,
      transform: emptyTransformFields,
      detection: 'filter-items' as const,
      openAdvanced: false,
    }),
  },
  {
    id: 'any-change-5m',
    label: 'Any change (5 min trial)',
    blurb: 'Hash the projected payload; wake on any change. Auto-stops after 5 minutes.',
    apply: () => ({
      name: 'trial-5m',
      intervalSec: '5',
      forSeconds: '300',
      wakeDelivery: 'when-idle',
      wakePrompt: 'Watched payload changed (5m trial). Inspect the fact lane.',
      queryEnabled: false,
      transform: emptyTransformFields,
      detection: 'any-change',
      openAdvanced: false,
    }),
  },
  {
    id: 'status-change',
    label: 'Status change',
    blurb: 'Thin status fields via map so hash ignores noise (or use selector only).',
    apply: () => {
      const tpl = watchTransformTemplates.find((x) => x.id === 'strip-jitter');
      return {
        name: 'status-change',
        laneId: 'unit_status',
        laneTouched: true,
        intervalSec: '5',
        wakeDelivery: 'when-idle',
        wakePrompt: 'Unit status changed. See unit_status lane.',
        queryEnabled: false,
        transform: tpl
          ? { code: tpl.code, bindingsText: '', timeoutMs: '5000' }
          : emptyTransformFields,
        detection: 'custom',
        openAdvanced: true,
      };
    },
  },
];

function slugTool(tool: string): string {
  return tool
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** Walk a JSON sample into selector-style paths (dots + [*] for arrays). */
function collectSamplePaths(
  value: unknown,
  base = '',
  out: { path: string; kind: 'value' | 'array' | 'object' }[] = [],
  depth = 0,
): { path: string; kind: 'value' | 'array' | 'object' }[] {
  if (depth > 6 || out.length > 200) return out;
  if (value === null || value === undefined) {
    if (base) out.push({ path: base, kind: 'value' });
    return out;
  }
  if (Array.isArray(value)) {
    if (base) out.push({ path: base, kind: 'array' });
    // Represent element projection once via [*]
    const star = base ? `${base}[*]` : '[*]';
    if (value.length === 0) {
      out.push({ path: star, kind: 'value' });
      return out;
    }
    // Sample a few elements' shapes via first element only
    collectSamplePaths(value[0], star, out, depth + 1);
    return out;
  }
  if (typeof value === 'object') {
    if (base) out.push({ path: base, kind: 'object' });
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (!/^[A-Za-z0-9_-]+$/.test(k)) continue;
      const next = base ? `${base}.${k}` : k;
      collectSamplePaths(v, next, out, depth + 1);
    }
    return out;
  }
  // primitive
  if (base) out.push({ path: base, kind: 'value' });
  return out;
}

function uniquePaths(
  entries: { path: string; kind: 'value' | 'array' | 'object' }[],
): { path: string; kind: 'value' | 'array' | 'object' }[] {
  const seen = new Set<string>();
  const out: typeof entries = [];
  for (const e of entries) {
    const key = `${e.kind}:${e.path}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(e);
  }
  return out;
}

/** Keys on the first element of an array at `itemsPath` ('' = root is array). */
function itemFieldKeys(sample: unknown, itemsPath: string): string[] {
  let cur: unknown = sample;
  const path = itemsPath.trim();
  if (path) {
    for (const part of path.split('.')) {
      if (!part || cur == null || typeof cur !== 'object' || Array.isArray(cur)) {
        cur = undefined;
        break;
      }
      cur = (cur as Record<string, unknown>)[part];
    }
  }
  if (!Array.isArray(cur) || cur.length === 0) return [];
  const first = cur[0];
  if (!first || typeof first !== 'object' || Array.isArray(first)) return [];
  return Object.keys(first as object)
    .filter((k) => /^[A-Za-z0-9_-]+$/.test(k))
    .slice(0, 40);
}

function toggleCsv(csv: string, key: string): string {
  const parts = csv
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const set = new Set(parts);
  if (set.has(key)) set.delete(key);
  else set.add(key);
  return [...set].join(', ');
}

export function WatchesPanel({
  agentId,
  threadId,
  status,
  /** Hide the existing-watch list (orchestration Create tab). */
  hideList = false,
  /** Open the attach wizard immediately (and when thread changes). */
  startInCreate = false,
  /** Optional callback after a successful create/save (e.g. jump back to fleet). */
  onSaved,
}: {
  agentId: string;
  threadId: string;
  status: string | null;
  hideList?: boolean;
  startInCreate?: boolean;
  onSaved?: () => void;
}) {
  const client = useClient();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(startInCreate);
  /** Name of the watch being edited, or null when attaching a new one. */
  const [editingName, setEditingName] = useState<string | null>(null);
  const [form, setForm] = useState<IWatchForm>(emptyForm);
  /** Last selector that parsed — keeps the projection rendered (dimmed) while typing. */
  const [lastGoodSelector, setLastGoodSelector] = useState('');
  /** The advanced transform section — auto-opens when editing a transform watch. */
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [showToolArgs, setShowToolArgs] = useState(false);
  const [setupPrompt, setSetupPrompt] = useState('');
  const [setupError, setSetupError] = useState<string | null>(null);
  const [setupNote, setSetupNote] = useState<string | null>(null);
  const lastAutoPreview = useRef('');

  const base = `v1/persistent-agent-groups/${agentId}/agents/${threadId}/watches`;
  const refresh = () => qc.invalidateQueries({ queryKey: ['persistent-agent-watches', threadId] });

  const watches = useQuery({
    queryKey: ['persistent-agent-watches', threadId],
    queryFn: () => client.http.get(base).json<PersistentAgentWatch[]>(),
  });

  const { connections, loadTools } = useWatchConnections(editing);

  const parsedArgs = useMemo((): { args?: Record<string, unknown>; error?: string } => {
    const r = parseJsonObjectText(form.argsText, 'Args');
    return r.error ? { error: r.error } : { args: r.value ?? {} };
  }, [form.argsText]);

  const codeTrim = form.transform.code.trim();

  /** Build declarative query from form, or null if incomplete/disabled. */
  const formQuery: WatchQuery | null = (() => {
    if (!form.queryEnabled) return null;
    const wherePath = form.queryWherePath.trim();
    const whereValRaw = form.queryWhereValue.trim();
    const select = form.querySelect
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const items = form.queryItems.trim();
    const hasWhere = wherePath !== '' && whereValRaw !== '';
    // Need at least a list path, a field pick, or a filter — not an empty query.
    if (!items && !hasWhere && select.length === 0) return null;
    const where = hasWhere
      ? {
          path: wherePath,
          op: form.queryWhereOp,
          value: Number.isFinite(Number(whereValRaw))
            ? Number(whereValRaw)
            : whereValRaw === 'true'
              ? true
              : whereValRaw === 'false'
                ? false
                : whereValRaw,
        }
      : undefined;
    return {
      ...(items ? { items } : {}),
      ...(where ? { where } : {}),
      ...(select.length ? { select } : {}),
      when: form.queryWhen,
    };
  })();
  const compiledQuery = (() => {
    if (!formQuery) return null as null | ReturnType<typeof compileWatchQuery> | { error: string };
    try {
      return compileWatchQuery(formQuery);
    } catch (err) {
      return { error: err instanceof Error ? err.message : String(err) };
    }
  })();
  const queryCompileError =
    form.queryEnabled && formQuery == null
      ? 'Pick a list and fields to keep (filter is optional)'
      : compiledQuery && 'error' in compiledQuery
        ? compiledQuery.error
        : null;
  /** Code actually sent on put/preview (query wins over freeform map). */
  const effectiveMapCode =
    compiledQuery && !('error' in compiledQuery)
      ? compiledQuery.code
      : form.queryEnabled
        ? ''
        : form.transform.code;
  const effectiveCodeTrim = effectiveMapCode.trim();
  const effectiveConditionFromQuery =
    compiledQuery && !('error' in compiledQuery) ? compiledQuery.condition : undefined;

  const parsedBindings = useMemo(
    () => parseJsonObjectText(form.transform.bindingsText, 'Bindings'),
    [form.transform.bindingsText],
  );
  const parsedTimeout = useMemo(
    () => parseTimeoutMs(form.transform.timeoutMs),
    [form.transform.timeoutMs],
  );

  const selectorTrim = form.selector.trim();
  const selectorInvalid = selectorTrim !== '' && parseWatchSelector(selectorTrim) === null;

  const preview = useMutation({
    mutationFn: () =>
      client.http
        .post(`${base}/preview`, {
          json: {
            mcpConnectionId: form.mcpConnectionId,
            toolName: form.toolName,
            args: parsedArgs.args ?? {},
            // The transform runs on the PROJECTED output server-side, so send
            // the selector along — the dry-run then matches the runner.
            // Query maps the full tool payload; sending a field selector here
            // would project first and break items paths like "drones".
            ...(!form.queryEnabled && selectorTrim && !selectorInvalid
              ? { selector: selectorTrim }
              : {}),
            ...(() => {
              if (form.conditionOp && form.conditionValue.trim() !== '') {
                return {
                  condition: {
                    op: form.conditionOp,
                    // Regex patterns must stay strings — never number-coerce.
                    value:
                      form.conditionOp === 'matches'
                        ? form.conditionValue.trim()
                        : Number.isFinite(Number(form.conditionValue))
                          ? Number(form.conditionValue)
                          : form.conditionValue === 'true'
                            ? true
                            : form.conditionValue === 'false'
                              ? false
                              : form.conditionValue,
                    ...(form.conditionPath.trim() ? { path: form.conditionPath.trim() } : {}),
                    risingEdge: form.conditionRising,
                  },
                };
              }
              if (effectiveConditionFromQuery) {
                return { condition: effectiveConditionFromQuery };
              }
              return {};
            })(),
            ...(effectiveCodeTrim
              ? {
                  code: effectiveMapCode,
                  ...(!form.queryEnabled && parsedBindings.value
                    ? { bindings: parsedBindings.value }
                    : {}),
                  ...(!form.queryEnabled && parsedTimeout.value
                    ? { timeoutMs: parsedTimeout.value }
                    : { timeoutMs: 5000 }),
                }
              : {}),
          },
        })
        .json<PreviewPersistentAgentWatchResult>(),
  });

  const intervalNum = Number(form.intervalSec);
  const intervalValid = Number.isInteger(intervalNum) && intervalNum >= 2 && intervalNum <= 3600;

  const put = useMutation({
    mutationFn: () => {
      const watchName = form.name.trim() || slugTool(form.toolName);
      if (!watchName) throw new Error('Watch name is required');
      const lane = form.laneId.trim() || slugTool(watchName);
      return client.http
        .put(`${base}/${encodeURIComponent(watchName)}`, {
          json: {
            mcpConnectionId: form.mcpConnectionId,
            toolName: form.toolName,
            args: parsedArgs.args ?? {},
            intervalSec: intervalNum,
            laneId: lane,
            wakeDelivery: form.wakeDelivery,
            ...(form.wakePrompt.trim() ? { wakePrompt: form.wakePrompt.trim() } : {}),
            ...(!form.queryEnabled && selectorTrim ? { selector: selectorTrim } : {}),
            ...(form.forSeconds.trim() !== '' && Number.isFinite(Number(form.forSeconds))
              ? { forSeconds: Math.trunc(Number(form.forSeconds)) }
              : {}),
            ...(() => {
              if (form.conditionOp && form.conditionValue.trim() !== '') {
                return {
                  condition: {
                    op: form.conditionOp,
                    // Regex patterns must stay strings — never number-coerce.
                    value:
                      form.conditionOp === 'matches'
                        ? form.conditionValue.trim()
                        : Number.isFinite(Number(form.conditionValue))
                          ? Number(form.conditionValue)
                          : form.conditionValue === 'true'
                            ? true
                            : form.conditionValue === 'false'
                              ? false
                              : form.conditionValue,
                    ...(form.conditionPath.trim() ? { path: form.conditionPath.trim() } : {}),
                    risingEdge: form.conditionRising,
                  },
                };
              }
              if (effectiveConditionFromQuery) {
                return { condition: effectiveConditionFromQuery };
              }
              return { condition: null };
            })(),
            // Upsert replaces the whole watch — omitting code drops map mode.
            // Query compiles to code; freeform map only when query is off.
            ...(effectiveCodeTrim
              ? {
                  code: effectiveMapCode,
                  ...(!form.queryEnabled && parsedBindings.value
                    ? { bindings: parsedBindings.value }
                    : {}),
                  timeoutMs: form.queryEnabled ? 5000 : (parsedTimeout.value ?? 5000),
                }
              : {}),
          },
        })
        .json<PersistentAgentWatch>();
    },
    onSuccess: () => {
      if (startInCreate && onSaved) {
        onSaved();
      } else if (!startInCreate) {
        setEditing(false);
      } else {
        // Stay in create mode with a fresh form for another attach.
        openWizard(null);
      }
      return refresh();
    },
  });

  const generateSetup = useMutation({
    mutationFn: async () => {
      const sample = preview.data?.ok === true ? preview.data.samples[0]?.output : undefined;
      try {
        return await client.http
          .post(`${base}/generate-setup`, {
            json: {
              mcpConnectionId: form.mcpConnectionId,
              prompt: setupPrompt.trim(),
              ...(form.toolName ? { toolName: form.toolName } : {}),
              ...(sample !== undefined ? { sampleOutput: sample } : {}),
            },
          })
          .json<GenerateWatchSetupResult>();
      } catch (err) {
        const e = err as { response?: Response };
        if (e.response) {
          try {
            const body = (await e.response.json()) as { message?: string | string[] };
            const msg = Array.isArray(body.message) ? body.message.join('; ') : body.message;
            throw new Error(msg || `Setup assist failed (${e.response.status})`);
          } catch (inner) {
            if (inner instanceof Error && !inner.message.startsWith('Setup assist failed ('))
              throw inner;
          }
        }
        throw err instanceof Error ? err : new Error(String(err));
      }
    },
    onSuccess: (res) => {
      setSetupError(null);
      setSetupNote(res.rationale ?? 'Filled from your description — review and save.');
      applyGeneratedSetup(res);
    },
    onError: (err) => {
      setSetupNote(null);
      setSetupError(err instanceof Error ? err.message : String(err));
    },
  });

  const setStatus = useMutation({
    mutationFn: ({ name, next }: { name: string; next: 'ACTIVE' | 'PAUSED' }) =>
      client.http
        .patch(`${base}/${encodeURIComponent(name)}`, { json: { status: next } })
        .json<PersistentAgentWatch>(),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: (name: string) => client.http.delete(`${base}/${encodeURIComponent(name)}`).text(),
    onSuccess: refresh,
  });

  const terminal = status === 'STOPPED' || status === 'FAILED';
  const rows = watches.data ?? [];

  function applyGeneratedSetup(res: GenerateWatchSetupResult) {
    const mode = res.mode;
    const q = res.query;
    setForm((f) => ({
      ...f,
      name: res.name || f.name,
      nameTouched: Boolean(res.name) || f.nameTouched,
      toolName: res.toolName || f.toolName,
      argsText: JSON.stringify(res.args ?? {}, null, 2),
      laneId: res.laneId || f.laneId || slugTool(res.name || f.name || res.toolName || f.toolName),
      laneTouched: Boolean(res.laneId) || f.laneTouched,
      wakePrompt: res.wakePrompt ?? f.wakePrompt,
      wakeDelivery: res.wakeDelivery ?? f.wakeDelivery,
      intervalSec: String(res.intervalSec ?? f.intervalSec ?? 5),
      forSeconds:
        res.forSeconds == null || res.forSeconds === undefined
          ? f.forSeconds
          : String(res.forSeconds),
      selector: mode === 'query' ? '' : (res.selector ?? ''),
      conditionOp:
        mode === 'value' && res.condition?.op
          ? (res.condition.op as IWatchForm['conditionOp'])
          : '',
      conditionValue: mode === 'value' && res.condition ? String(res.condition.value) : '',
      conditionPath: '',
      conditionRising: mode === 'value' ? (res.conditionRising ?? false) : true,
      queryEnabled: mode === 'query',
      queryItems: q?.items ?? '',
      queryWherePath: q?.where?.path ?? '',
      queryWhereOp: (q?.where?.op as IWatchForm['queryWhereOp']) ?? 'lt',
      queryWhereValue: q?.where != null ? String(q.where.value) : '',
      querySelect: (q?.select ?? []).join(', '),
      queryWhen: q?.when ?? 'changed',
      transform:
        mode === 'map' && res.code
          ? {
              code: res.code,
              bindingsText: '',
              timeoutMs: '5000',
            }
          : emptyTransformFields,
    }));
    setLastGoodSelector(mode === 'query' ? '' : (res.selector ?? ''));
    setAdvancedOpen(mode === 'map');
  }

  function openWizard(watch: PersistentAgentWatch | null) {
    setForm(
      watch
        ? {
            name: watch.name,
            nameTouched: true,
            mcpConnectionId: watch.mcpConnectionId,
            toolName: watch.toolName,
            argsText: JSON.stringify(watch.args ?? {}, null, 2),
            intervalSec: String(watch.intervalSec),
            laneId: watch.laneId ?? '',
            laneTouched: true,
            wakePrompt: watch.wakePrompt ?? '',
            wakeDelivery: watch.wakeDelivery,
            selector: watch.selector ?? '',
            forSeconds: watch.expiresAt
              ? String(
                  Math.max(
                    60,
                    Math.round((new Date(watch.expiresAt).getTime() - Date.now()) / 1000),
                  ),
                )
              : '',
            ...(() => {
              const parsedQ = parseCompiledWatchQuery(watch.code);
              const c = watch.condition as
                | {
                    op?: string;
                    value?: unknown;
                    path?: string;
                    risingEdge?: boolean;
                  }
                | null
                | undefined;

              // Infer query mode from compiled map body.
              if (parsedQ) {
                const nonempty =
                  c?.op === 'gt' && (c.value === 0 || c.value === '0') && c.risingEdge === true;
                return {
                  // Scalar condition UI off — gate lives in query.when
                  conditionOp: '' as const,
                  conditionValue: '',
                  conditionPath: '',
                  conditionRising: false,
                  queryEnabled: true,
                  queryItems: parsedQ.items ?? '',
                  queryWherePath: parsedQ.where?.path ?? '',
                  queryWhereOp: parsedQ.where?.op ?? 'lt',
                  queryWhereValue: parsedQ.where != null ? String(parsedQ.where.value) : '',
                  querySelect: (parsedQ.select ?? []).join(', '),
                  queryWhen: nonempty ? 'nonempty' : 'changed',
                  // Don't show compiled JS in the custom editor
                  transform: {
                    code: '',
                    bindingsText: '',
                    timeoutMs: String(watch.timeoutMs ?? 5000),
                  },
                };
              }

              if (!c || !c.op) {
                return {
                  conditionOp: '' as const,
                  conditionValue: '',
                  conditionPath: '',
                  conditionRising: false,
                  queryEnabled: false,
                  queryItems: '',
                  queryWherePath: '',
                  queryWhereOp: 'lt' as const,
                  queryWhereValue: '30',
                  querySelect: '',
                  queryWhen: 'changed' as const,
                  transform: {
                    code: watch.code ?? '',
                    bindingsText: watch.bindings ? JSON.stringify(watch.bindings, null, 2) : '',
                    timeoutMs: String(watch.timeoutMs ?? 5000),
                  },
                };
              }
              return {
                conditionOp: c.op as IWatchForm['conditionOp'],
                conditionValue: c.value == null ? '' : String(c.value),
                conditionPath: c.path ?? '',
                conditionRising: c.risingEdge === true,
                queryEnabled: false,
                queryItems: '',
                queryWherePath: '',
                queryWhereOp: 'lt' as const,
                queryWhereValue: '30',
                querySelect: '',
                queryWhen: 'changed' as const,
                transform: {
                  code: watch.code ?? '',
                  bindingsText: watch.bindings ? JSON.stringify(watch.bindings, null, 2) : '',
                  timeoutMs: String(watch.timeoutMs ?? 5000),
                },
              };
            })(),
          }
        : emptyForm,
    );
    setEditingName(watch?.name ?? null);
    setLastGoodSelector(watch?.selector ?? '');
    const code = watch?.code ?? '';
    const fromQuery = Boolean(parseCompiledWatchQuery(code));
    // Freeform map only when there is real hand-written code
    setAdvancedOpen(Boolean(code) && !fromQuery);
    setShowToolArgs(Boolean(watch?.args && Object.keys(watch.args as object).length > 0));
    setSetupPrompt('');
    setSetupError(null);
    setSetupNote(null);
    put.reset();
    preview.reset();
    loadTools.reset();
    lastAutoPreview.current = '';
    setEditing(true);
  }

  /** Keep `lastGoodSelector` on every parseable keystroke (empty = whole response). */
  function changeSelector(next: string) {
    setForm((f) => ({ ...f, selector: next }));
    const t = next.trim();
    if (t === '' || parseWatchSelector(t) !== null) setLastGoodSelector(t);
  }

  /**
   * Tree click in filter mode: list path → items; list.field / [*] .field → where field.
   * Never sets the watch selector (query uses the full payload).
   */
  function applyFilterPathClick(path: string) {
    const p = path.trim();
    if (!p) return;
    // drones[*].battery → items=drones, where=battery
    const starField = /^(.*)\[\*\]\.([A-Za-z0-9_-]+)$/.exec(p);
    if (starField) {
      const items = starField[1] ?? '';
      const field = starField[2] ?? '';
      setForm((f) => ({
        ...f,
        selector: '',
        queryItems: items,
        queryWherePath: field,
        querySelect:
          f.querySelect.trim() ||
          (field ? `${field.includes('battery') ? 'drone_id, ' : ''}${field}` : f.querySelect),
      }));
      return;
    }
    // drones.battery (unlikely) or plain array path "drones"
    if (arrayPathOptions.some((o) => o.path === p) || p.endsWith('[*]')) {
      const items = p.endsWith('[*]') ? p.slice(0, -3) : p;
      setForm((f) => ({
        ...f,
        selector: '',
        queryItems: items,
        queryWherePath: '',
        querySelect: '',
      }));
      return;
    }
    // nested under known items: drones.foo already handled; field-only under current items
    const items = form.queryItems.trim();
    if (items && p.startsWith(items + '.')) {
      const field = p.slice(items.length + 1).split('.')[0] ?? '';
      if (field) {
        setForm((f) => ({ ...f, selector: '', queryWherePath: field }));
        return;
      }
    }
    // bare field name while items already chosen
    if (items && !p.includes('.') && !p.includes('[')) {
      setForm((f) => ({ ...f, selector: '', queryWherePath: p }));
      return;
    }
    // fallback: treat as items path
    setForm((f) => ({
      ...f,
      selector: '',
      queryItems: p.replace(/\[\*\]$/, ''),
    }));
  }

  function applyRecipe(recipe: IWatchRecipe) {
    const patch = recipe.apply();
    const { detection: _d, openAdvanced, ...fields } = patch;
    setForm((f) => {
      const next = { ...f, ...fields };
      // Keep connection/tool if already chosen; only fill empty name/lane from recipe.
      if (f.mcpConnectionId) next.mcpConnectionId = f.mcpConnectionId;
      if (f.toolName) {
        next.toolName = f.toolName;
        if (!fields.nameTouched && !f.nameTouched && !next.name) {
          next.name = slugTool(f.toolName);
        }
        if (!fields.laneTouched && !f.laneTouched) {
          next.laneId = f.laneId || slugTool(next.name || f.name || f.toolName);
        }
      }
      return next;
    });
    if (openAdvanced != null) setAdvancedOpen(openAdvanced);
    put.reset();
    preview.reset();
  }

  function applyDetectionPreset(preset: DetectionPreset) {
    if (preset === 'any-change') {
      setForm((f) => ({
        ...f,
        queryEnabled: false,
        queryItems: '',
        queryWherePath: '',
        querySelect: '',
        transform: { ...emptyTransformFields, timeoutMs: f.transform.timeoutMs },
      }));
      setAdvancedOpen(false);
      return;
    }
    if (preset === 'filter-items') {
      setForm((f) => ({
        ...f,
        // Query runs on the full tool payload — a leftover field selector
        // (e.g. drones[*].battery) would project away the array and yield [].
        selector: '',
        queryEnabled: true,
        queryItems: f.queryItems || 'drones',
        // Filter optional — thin-list is items + select only
        queryWherePath: f.queryWherePath,
        queryWhereOp: f.queryWhereOp || 'lt',
        queryWhereValue: f.queryWhereValue,
        querySelect: f.querySelect || 'drone_id, battery',
        queryWhen: f.queryWhen || 'changed',
        transform: { ...emptyTransformFields, timeoutMs: f.transform.timeoutMs },
        wakeDelivery: 'when-idle',
      }));
      setAdvancedOpen(false);
      return;
    }
    // custom map JS
    setForm((f) => ({
      ...f,
      queryEnabled: false,
      transform: f.transform.code.trim()
        ? f.transform
        : {
            code: '/** Pure map — return shaped payload or undefined to skip. */\nreturn input.output;',
            bindingsText: '',
            timeoutMs: f.transform.timeoutMs || '5000',
          },
    }));
    setAdvancedOpen(true);
  }

  // Auto-preview when connection+tool become ready (new attach only).
  const autoPreviewKey = `${form.mcpConnectionId}|${form.toolName}|${editingName ?? ''}`;
  useEffect(() => {
    if (!editing || editingName !== null) return;
    if (!form.mcpConnectionId || !form.toolName || parsedArgs.error) return;
    if (lastAutoPreview.current === autoPreviewKey) return;
    lastAutoPreview.current = autoPreviewKey;
    const handle = window.setTimeout(() => {
      preview.mutate();
    }, 400);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberate key
  }, [autoPreviewKey, editing, editingName, parsedArgs.error]);

  // Orchestration Create tab: always show the wizard for this thread.
  useEffect(() => {
    if (!startInCreate) return;
    if (!threadId || !agentId) return;
    openWizard(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open on target change only
  }, [startInCreate, threadId, agentId]);

  // Single MCP connection → preselect when opening empty wizard.
  useEffect(() => {
    if (!editing || editingName !== null) return;
    if (form.mcpConnectionId) return;
    const items = connections.data?.items ?? [];
    const only = items.length === 1 ? items[0] : undefined;
    if (only) {
      setForm((f) => ({ ...f, mcpConnectionId: only.id }));
    }
  }, [editing, editingName, form.mcpConnectionId, connections.data?.items]);

  const forSecondsNum = form.forSeconds.trim() === '' ? null : Number(form.forSeconds);
  const forSecondsValid =
    forSecondsNum === null ||
    (Number.isInteger(forSecondsNum) && forSecondsNum >= 60 && forSecondsNum <= 30 * 24 * 3600);

  const wakeMode: DetectionPreset = form.queryEnabled
    ? 'filter-items'
    : codeTrim
      ? 'custom'
      : 'any-change';

  const opWord: Record<IWatchForm['queryWhereOp'], string> = {
    lt: 'is below',
    lte: 'is at most',
    gt: 'is above',
    gte: 'is at least',
    eq: 'equals',
    neq: 'is not',
  };

  const condWord: Record<Exclude<IWatchForm['conditionOp'], ''>, string> = {
    gt: 'is larger than',
    gte: 'is at least',
    lt: 'is smaller than',
    lte: 'is at most',
    eq: 'equals',
    neq: 'is not',
    matches: 'matches regex',
  };

  const deliveryWord: Record<PersistentAgentWatchDelivery, string> = {
    'next-turn': 'after current work finishes',
    'when-idle': 'only when the agent is free',
    inbox: 'quietly (facts only, no forced turn)',
  };

  const summaryLine = (() => {
    const tool = form.toolName || 'the tool';
    if (form.queryEnabled) {
      const items = form.queryItems.trim() || 'items';
      const keep = form.querySelect.trim() || 'each full item';
      const when =
        form.queryWhen === 'nonempty'
          ? 'the first time the list is non-empty'
          : 'whenever that list changes';
      const wherePath = form.queryWherePath.trim();
      const whereVal = form.queryWhereValue.trim();
      if (wherePath && whereVal) {
        const op = opWord[form.queryWhereOp];
        return `Watch ${tool}: from ${items}, keep rows where ${wherePath} ${op} ${whereVal} (store ${keep}). Wake ${when}, ${deliveryWord[form.wakeDelivery]}.`;
      }
      return `Watch ${tool}: thin ${items} to ${keep}. Wake ${when}, ${deliveryWord[form.wakeDelivery]}.`;
    }
    if (codeTrim) {
      return `Watch ${tool} with a custom map. Wake when the mapped value changes, ${deliveryWord[form.wakeDelivery]}.`;
    }
    const sel = form.selector.trim() || 'the full response';
    if (form.conditionOp && form.conditionValue.trim() !== '') {
      const cw = condWord[form.conditionOp];
      const edge = form.conditionRising
        ? `only the first time it ${cw}`
        : `on every change while it ${cw}`;
      return `Watch ${sel} on ${tool}. Wake ${edge} ${form.conditionValue.trim()}, ${deliveryWord[form.wakeDelivery]}.`;
    }
    return `Watch ${sel} on ${tool}. Wake whenever it changes, ${deliveryWord[form.wakeDelivery]}.`;
  })();

  const previewSample = preview.data?.ok === true ? preview.data.samples[0]?.output : undefined;
  const samplePathEntries =
    previewSample === undefined ? [] : uniquePaths(collectSamplePaths(previewSample));
  const valueFieldOptions = samplePathEntries.filter(
    (e) => e.kind === 'value' || e.kind === 'array',
  );
  const arrayPathOptions = samplePathEntries.filter((e) => e.kind === 'array');
  const itemKeys = previewSample === undefined ? [] : itemFieldKeys(previewSample, form.queryItems);
  const selectedKeep = new Set(
    form.querySelect
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
  );

  const argsAreCustom = form.argsText.trim() !== '' && form.argsText.trim() !== '{}';

  return (
    <section className="card" style={{ marginTop: hideList ? 0 : 16 }}>
      {!hideList && (
        <div
          className="card-header"
          style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}
        >
          <span className="card-title">Watches</span>
          {rows.length > 0 && <span className="badge badge--neutral">{rows.length}</span>}
          {!hideList && !editing && !terminal && (
            <button
              className="btn btn--secondary btn--sm"
              style={{ marginLeft: 'auto' }}
              onClick={() => openWizard(null)}
            >
              Attach watch…
            </button>
          )}
        </div>
      )}
      <div className="card-body">
        {!hideList && terminal && rows.length === 0 && !editing ? (
          <span className="t-muted t-sm">This thread is stopped — no watches to attach.</span>
        ) : !hideList && rows.length === 0 && !editing ? (
          <span className="t-sm t-muted">
            No watches yet — this is the preferred place to arm sensors for this thread (missions,
            clear-until-zero, timed trials). Without a watch the agent only sees tool data when it
            polls. Attach a watch to push changes into a fact lane and wake the thread instead.
          </span>
        ) : null}

        {!hideList &&
          rows.map((w) => (
            <WatchRow
              key={w.id}
              watch={w}
              pending={setStatus.isPending || remove.isPending}
              onToggle={() =>
                setStatus.mutate({
                  name: w.name,
                  next: w.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE',
                })
              }
              onEdit={() => openWizard(w)}
              onDelete={() => {
                if (confirm(`Delete watch "${w.name}"? The "${w.laneId}" lane stops updating.`))
                  remove.mutate(w.name);
              }}
            />
          ))}
        {!hideList && (setStatus.isError || remove.isError) && (
          <div className="alert alert--danger" style={{ marginTop: 8 }}>
            {String(((setStatus.error || remove.error) as Error)?.message ?? 'Update failed')}
          </div>
        )}

        {editing && (
          <div className="pa-watch-wiz" style={{ marginTop: rows.length > 0 ? 12 : 0 }}>
            {editingName === null && (
              <div>
                <div className="t-xs t-muted" style={{ marginBottom: 8 }}>
                  Start from a recipe, or build below
                </div>
                <div className="pa-watch-recipe-grid">
                  {watchRecipes.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      className="pa-watch-recipe"
                      onClick={() => applyRecipe(r)}
                    >
                      <span className="pa-watch-recipe__title">{r.label}</span>
                      <span className="pa-watch-recipe__blurb">{r.blurb}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="pa-watch-step">
              <div className="pa-watch-step__head">
                <span className="pa-watch-step__num">1</span>
                <span className="pa-watch-step__title">What should we poll?</span>
              </div>
              <p className="pa-watch-step__sub">
                Name is the watch id on this thread (unique). Tool is what we poll — many watches
                can share one tool. Lane defaults from the name.
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <label className="field" style={{ flex: '0 0 150px' }}>
                  <span className="field-label">Name (unique)</span>
                  <input
                    className="input"
                    value={form.name}
                    maxLength={64}
                    disabled={editingName !== null}
                    placeholder="e.g. open-events"
                    title="Watch identity on this thread — not the tool name. Two watches can share a tool."
                    onChange={(e) => {
                      const name = e.target.value;
                      setForm((f) => ({
                        ...f,
                        name,
                        nameTouched: true,
                        // Keep lane tied to name until operator edits lane.
                        laneId: f.laneTouched ? f.laneId : slugTool(name || f.toolName),
                      }));
                    }}
                  />
                </label>
                <label className="field" style={{ flex: 1, minWidth: 160 }}>
                  <span className="field-label">Connection</span>
                  <select
                    className="select-native"
                    value={form.mcpConnectionId}
                    disabled={connections.isLoading}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, mcpConnectionId: e.target.value, toolName: '' }));
                      preview.reset();
                      loadTools.reset();
                    }}
                  >
                    <option value="">Choose…</option>
                    {(connections.data?.items ?? []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                        {c.authType === 'oauth' && !c.hasKeyring ? ' · not authed' : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <ToolField
                  connection={
                    (connections.data?.items ?? []).find((c) => c.id === form.mcpConnectionId) ??
                    null
                  }
                  value={form.toolName}
                  loadTools={loadTools}
                  onChange={(tool) => {
                    setForm((f) => {
                      const nextName = f.nameTouched || f.name.trim() ? f.name : slugTool(tool);
                      const nextLane = f.laneTouched ? f.laneId : slugTool(nextName || tool);
                      return {
                        ...f,
                        toolName: tool,
                        name: nextName,
                        laneId: nextLane,
                      };
                    });
                    preview.reset();
                  }}
                />
              </div>
              <div
                style={{
                  marginTop: 14,
                  padding: '10px 12px',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 8,
                  background: 'var(--surface-1, rgba(255,255,255,0.03))',
                }}
              >
                <div className="field-label" style={{ marginBottom: 6 }}>
                  Describe the watch (AI fill)
                </div>
                <p className="t-xs t-muted" style={{ margin: '0 0 8px' }}>
                  Pick a connection first. Optionally sample a tool so field names match. Then ask
                  in plain English — we&apos;ll choose tool, mode, fields, and delivery.
                </p>
                <textarea
                  className="input"
                  rows={2}
                  value={setupPrompt}
                  placeholder='e.g. "Wake when any drone battery drops below 30 and store drone_id + battery"'
                  onChange={(e) => setSetupPrompt(e.target.value)}
                  style={{ resize: 'vertical', marginBottom: 8 }}
                />
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="btn btn--secondary btn--sm"
                    disabled={
                      !form.mcpConnectionId ||
                      setupPrompt.trim().length < 4 ||
                      generateSetup.isPending
                    }
                    onClick={() => generateSetup.mutate()}
                  >
                    {generateSetup.isPending ? 'Thinking…' : 'Fill wizard with AI'}
                  </button>
                  {setupNote && (
                    <span className="t-xs t-muted" style={{ flex: '1 1 200px' }}>
                      {setupNote}
                    </span>
                  )}
                  {setupError && (
                    <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
                      {setupError}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="pa-watch-adv-link"
                  onClick={() => setShowToolArgs((v) => !v)}
                >
                  {showToolArgs || argsAreCustom ? 'Hide tool args' : 'Need tool args?'}
                </button>
                {(showToolArgs || argsAreCustom) && (
                  <label className="field" style={{ marginTop: 6 }}>
                    <span className="field-label">Args (JSON)</span>
                    <textarea
                      className="input t-mono"
                      rows={2}
                      value={form.argsText}
                      onChange={(e) => setForm((f) => ({ ...f, argsText: e.target.value }))}
                      style={{ resize: 'vertical' }}
                    />
                    {parsedArgs.error && (
                      <span className="field-help" style={{ color: 'var(--status-danger)' }}>
                        {parsedArgs.error}
                      </span>
                    )}
                  </label>
                )}
              </div>
            </div>

            <div className="pa-watch-step">
              <div className="pa-watch-step__head">
                <span className="pa-watch-step__num">2</span>
                <span className="pa-watch-step__title">What are we watching for?</span>
              </div>
              <div className="pa-watch-mode-grid">
                <button
                  type="button"
                  className={
                    wakeMode === 'any-change' ? 'pa-watch-mode pa-watch-mode--on' : 'pa-watch-mode'
                  }
                  onClick={() => applyDetectionPreset('any-change')}
                >
                  <span className="pa-watch-mode__label">A value changes</span>
                  <span className="pa-watch-mode__hint">
                    Best for counts and statuses — pick a field below.
                  </span>
                </button>
                <button
                  type="button"
                  className={
                    wakeMode === 'filter-items'
                      ? 'pa-watch-mode pa-watch-mode--on'
                      : 'pa-watch-mode'
                  }
                  onClick={() => applyDetectionPreset('filter-items')}
                >
                  <span className="pa-watch-mode__label">Thin a list</span>
                  <span className="pa-watch-mode__hint">
                    Keep id + battery on each drone; optional filter (e.g. battery &lt; 30).
                  </span>
                </button>
                <button
                  type="button"
                  className={
                    wakeMode === 'custom' ? 'pa-watch-mode pa-watch-mode--on' : 'pa-watch-mode'
                  }
                  onClick={() => applyDetectionPreset('custom')}
                >
                  <span className="pa-watch-mode__label">Custom reshape</span>
                  <span className="pa-watch-mode__hint">
                    Write a small map if the two options above aren&apos;t enough.
                  </span>
                </button>
              </div>

              {/* Sample first so dropdowns + tree drive the filter/selector */}
              <div style={{ marginTop: 14 }}>
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    marginBottom: 8,
                  }}
                >
                  <button
                    className="btn btn--secondary btn--sm"
                    disabled={
                      !form.mcpConnectionId ||
                      !form.toolName ||
                      parsedArgs.error !== undefined ||
                      queryCompileError != null ||
                      preview.isPending
                    }
                    onClick={() => preview.mutate()}
                  >
                    {preview.isPending ? 'Sampling…' : 'Sample tool'}
                  </button>
                  <span className="t-xs t-muted">
                    Sample once so lists and fields show up as dropdowns
                  </span>
                </div>
                {preview.data?.ok === false && (
                  <div className="alert alert--danger" style={{ marginBottom: 8 }}>
                    {preview.data.error ?? 'Preview failed'}
                  </div>
                )}
                {preview.isError && (
                  <div className="alert alert--danger" style={{ marginBottom: 8 }}>
                    {String((preview.error as Error)?.message ?? 'Preview failed')}
                  </div>
                )}
                {preview.data?.ok === true && preview.data.samples.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div className="t-xs t-muted" style={{ marginBottom: 6 }}>
                      {wakeMode === 'any-change'
                        ? 'Click a field to watch it (or use the dropdown below)'
                        : wakeMode === 'filter-items'
                          ? 'Click a list path to set “In list”, or a field under it for “where”'
                          : 'Sample output'}
                    </div>
                    <PreviewExplorer
                      result={preview.data}
                      selector={form.selector}
                      selectorInvalid={selectorInvalid}
                      lastGoodSelector={lastGoodSelector}
                      onSelectorChange={(path) => {
                        if (wakeMode === 'filter-items') {
                          applyFilterPathClick(path);
                          return;
                        }
                        changeSelector(path);
                      }}
                    />
                  </div>
                )}
                {preview.data?.ok === true && preview.data.transform && (
                  <div style={{ marginBottom: 12 }}>
                    <TransformResultPanel transform={preview.data.transform} />
                  </div>
                )}
              </div>

              {wakeMode === 'any-change' && (
                <div style={{ marginTop: 4 }}>
                  <label className="field" style={{ maxWidth: 420 }}>
                    <span className="field-label">Field to watch</span>
                    <select
                      className="select-native"
                      value={
                        form.selector === '' ||
                        valueFieldOptions.some((o) => o.path === form.selector)
                          ? form.selector
                          : '__custom__'
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '__custom__') return;
                        changeSelector(v);
                      }}
                    >
                      <option value="">
                        {valueFieldOptions.length === 0
                          ? 'Sample the tool to pick a field…'
                          : 'Whole response'}
                      </option>
                      {valueFieldOptions.map((o) => (
                        <option key={o.path} value={o.path}>
                          {o.path}
                          {o.kind === 'array' ? ' (list)' : ''}
                        </option>
                      ))}
                      {form.selector &&
                        !valueFieldOptions.some((o) => o.path === form.selector) && (
                          <option value="__custom__">{form.selector} (custom)</option>
                        )}
                    </select>
                  </label>
                  {selectorInvalid && (
                    <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
                      Invalid path — use dots and [0] / [*]
                    </span>
                  )}
                  <div style={{ marginTop: 14 }}>
                    <div className="field-label" style={{ marginBottom: 6 }}>
                      Only care when the value is…
                    </div>
                    <div className="pa-watch-mode-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                      <button
                        type="button"
                        className={
                          !form.conditionOp ? 'pa-watch-mode pa-watch-mode--on' : 'pa-watch-mode'
                        }
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            conditionOp: '',
                            conditionValue: '',
                          }))
                        }
                      >
                        <span className="pa-watch-mode__label">Any change</span>
                        <span className="pa-watch-mode__hint">
                          Wake whenever this field moves (5→6, 40→39, …).
                        </span>
                      </button>
                      <button
                        type="button"
                        className={
                          form.conditionOp ? 'pa-watch-mode pa-watch-mode--on' : 'pa-watch-mode'
                        }
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            conditionOp: f.conditionOp || 'gt',
                            conditionValue: f.conditionValue || '30',
                            // Default: every change while still past the gate
                            // (event count moves but only while &gt; 30).
                            conditionRising: f.conditionOp ? f.conditionRising : false,
                          }))
                        }
                      >
                        <span className="pa-watch-mode__label">Only while past a gate</span>
                        <span className="pa-watch-mode__hint">
                          e.g. count larger than 30 — ignore changes while ≤ 30.
                        </span>
                      </button>
                    </div>
                  </div>
                  {form.conditionOp && (
                    <div style={{ marginTop: 10 }}>
                      <div className="pa-watch-sentence">
                        <span>Value</span>
                        <select
                          className="select-native pa-watch-sentence__field--wide"
                          value={form.conditionOp}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              conditionOp: e.target.value as IWatchForm['conditionOp'],
                            }))
                          }
                        >
                          <option value="gt">is larger than</option>
                          <option value="gte">is at least</option>
                          <option value="lt">is smaller than</option>
                          <option value="lte">is at most</option>
                          <option value="eq">equals</option>
                          <option value="neq">is not</option>
                          <option value="matches">matches regex</option>
                        </select>
                        <input
                          className="input pa-watch-sentence__field"
                          value={form.conditionValue}
                          placeholder="30"
                          onChange={(e) =>
                            setForm((f) => ({ ...f, conditionValue: e.target.value }))
                          }
                        />
                      </div>
                      <div className="pa-watch-sentence" style={{ marginTop: 8 }}>
                        <span>Then wake</span>
                        <select
                          className="select-native pa-watch-sentence__field--xl"
                          value={form.conditionRising ? 'first' : 'each'}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              conditionRising: e.target.value === 'first',
                            }))
                          }
                        >
                          <option value="each">on every change while still past the gate</option>
                          <option value="first">only the first time it becomes true</option>
                        </select>
                      </div>
                      <p className="t-xs t-muted" style={{ margin: '8px 0 0' }}>
                        Example: field <code>events_active</code>, larger than 30, every change →
                        wake on 31→40→55, silent while count is 0–30.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {wakeMode === 'filter-items' && (
                <div style={{ marginTop: 4 }}>
                  {(() => {
                    const matchOnly = Boolean(
                      form.queryWherePath.trim() && form.queryWhereValue.trim(),
                    );
                    return (
                      <>
                        <div className="pa-watch-sentence">
                          <span>List</span>
                          <select
                            className="select-native pa-watch-sentence__field--wide"
                            value={
                              arrayPathOptions.some((o) => o.path === form.queryItems) ||
                              form.queryItems === ''
                                ? form.queryItems
                                : '__custom_items__'
                            }
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '__custom_items__') return;
                              setForm((f) => ({
                                ...f,
                                queryItems: v,
                                querySelect: f.querySelect,
                              }));
                            }}
                          >
                            <option value="">
                              {arrayPathOptions.length === 0
                                ? 'Sample to detect lists…'
                                : 'Root is the list'}
                            </option>
                            {arrayPathOptions.map((o) => (
                              <option key={o.path} value={o.path}>
                                {o.path}
                              </option>
                            ))}
                            {form.queryItems &&
                              !arrayPathOptions.some((o) => o.path === form.queryItems) && (
                                <option value="__custom_items__">{form.queryItems}</option>
                              )}
                          </select>
                        </div>

                        <div style={{ marginTop: 12 }}>
                          <div className="field-label" style={{ marginBottom: 6 }}>
                            Which items?
                          </div>
                          <div
                            className="pa-watch-mode-grid"
                            style={{ gridTemplateColumns: '1fr 1fr' }}
                          >
                            <button
                              type="button"
                              className={
                                !matchOnly ? 'pa-watch-mode pa-watch-mode--on' : 'pa-watch-mode'
                              }
                              onClick={() =>
                                setForm((f) => ({
                                  ...f,
                                  queryWherePath: '',
                                  queryWhereValue: '',
                                }))
                              }
                            >
                              <span className="pa-watch-mode__label">All items</span>
                              <span className="pa-watch-mode__hint">
                                Every row in the list (thin to chosen fields).
                              </span>
                            </button>
                            <button
                              type="button"
                              className={
                                matchOnly ? 'pa-watch-mode pa-watch-mode--on' : 'pa-watch-mode'
                              }
                              onClick={() =>
                                setForm((f) => ({
                                  ...f,
                                  queryWherePath: f.queryWherePath || itemKeys[0] || 'battery',
                                  queryWhereOp: f.queryWhereOp || 'lt',
                                  queryWhereValue: f.queryWhereValue || '30',
                                }))
                              }
                            >
                              <span className="pa-watch-mode__label">Only items that match</span>
                              <span className="pa-watch-mode__hint">
                                e.g. battery below 30 — drop the rest.
                              </span>
                            </button>
                          </div>
                        </div>

                        {matchOnly && (
                          <div className="pa-watch-sentence" style={{ marginTop: 10 }}>
                            <span>where</span>
                            <select
                              className="select-native pa-watch-sentence__field--wide"
                              value={
                                itemKeys.includes(form.queryWherePath) || form.queryWherePath === ''
                                  ? form.queryWherePath
                                  : '__custom_where__'
                              }
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === '__custom_where__') return;
                                setForm((f) => ({ ...f, queryWherePath: v }));
                              }}
                            >
                              <option value="">
                                {itemKeys.length === 0 ? 'Field…' : 'Choose field…'}
                              </option>
                              {itemKeys.map((k) => (
                                <option key={k} value={k}>
                                  {k}
                                </option>
                              ))}
                              {form.queryWherePath && !itemKeys.includes(form.queryWherePath) && (
                                <option value="__custom_where__">{form.queryWherePath}</option>
                              )}
                            </select>
                            <select
                              className="select-native pa-watch-sentence__op"
                              value={form.queryWhereOp}
                              onChange={(e) =>
                                setForm((f) => ({
                                  ...f,
                                  queryWhereOp: e.target.value as IWatchForm['queryWhereOp'],
                                }))
                              }
                            >
                              <option value="lt">&lt;</option>
                              <option value="lte">≤</option>
                              <option value="gt">&gt;</option>
                              <option value="gte">≥</option>
                              <option value="eq">=</option>
                              <option value="neq">≠</option>
                            </select>
                            <input
                              className="input pa-watch-sentence__field"
                              value={form.queryWhereValue}
                              placeholder="30"
                              onChange={(e) =>
                                setForm((f) => ({ ...f, queryWhereValue: e.target.value }))
                              }
                            />
                          </div>
                        )}

                        <div style={{ marginTop: 12 }}>
                          <div className="field-label" style={{ marginBottom: 6 }}>
                            Store on each {matchOnly ? 'match' : 'item'}
                          </div>
                          {itemKeys.length === 0 ? (
                            <span className="t-xs t-muted">
                              Sample the tool (and pick a list) to choose fields.
                            </span>
                          ) : (
                            <div className="pa-watch-multiselect">
                              <button
                                type="button"
                                className={
                                  selectedKeep.size === 0
                                    ? 'pa-watch-chip pa-watch-chip--on'
                                    : 'pa-watch-chip'
                                }
                                onClick={() => setForm((f) => ({ ...f, querySelect: '' }))}
                              >
                                {selectedKeep.size === 0 ? '✓ ' : ''}
                                Whole item
                              </button>
                              {itemKeys.map((k) => {
                                const on = selectedKeep.has(k);
                                return (
                                  <button
                                    key={k}
                                    type="button"
                                    className={
                                      on ? 'pa-watch-chip pa-watch-chip--on' : 'pa-watch-chip'
                                    }
                                    onClick={() =>
                                      setForm((f) => ({
                                        ...f,
                                        querySelect: toggleCsv(f.querySelect, k),
                                      }))
                                    }
                                  >
                                    {on ? '✓ ' : ''}
                                    {k}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        <div className="pa-watch-sentence" style={{ marginTop: 12 }}>
                          <span>Wake when</span>
                          <select
                            className="select-native pa-watch-sentence__field--xl"
                            value={form.queryWhen}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                queryWhen: e.target.value as IWatchForm['queryWhen'],
                              }))
                            }
                          >
                            <option value="changed">
                              {matchOnly
                                ? 'the set of matching items changes'
                                : 'the (thinned) list changes'}
                            </option>
                            <option value="nonempty">
                              {matchOnly
                                ? 'matching items first appear'
                                : 'the list first becomes non-empty'}
                            </option>
                          </select>
                        </div>
                        {queryCompileError ? (
                          <span
                            className="t-xs"
                            style={{
                              color: 'var(--status-danger)',
                              display: 'block',
                              marginTop: 8,
                            }}
                          >
                            {queryCompileError}
                          </span>
                        ) : null}
                      </>
                    );
                  })()}
                </div>
              )}

              {wakeMode === 'custom' && (
                <div style={{ marginTop: 4 }}>
                  <p className="pa-watch-step__sub">
                    Return the value to store (or undefined to skip). Prefer the other modes when
                    you can.
                  </p>
                  <TransformEditor
                    value={form.transform}
                    onChange={(patch) =>
                      setForm((f) => ({ ...f, transform: { ...f.transform, ...patch } }))
                    }
                    sampleOutput={
                      preview.data?.ok === true ? preview.data.samples[0]?.output : undefined
                    }
                    selector={undefined}
                    toolName={form.toolName || undefined}
                    onGenerate={async ({ prompt, sampleOutput, selector, bindings }) => {
                      try {
                        return await client.http
                          .post(`${base}/generate-transform`, {
                            json: {
                              prompt,
                              ...(sampleOutput !== undefined ? { sampleOutput } : {}),
                              ...(selector ? { selector } : {}),
                              ...(bindings ? { bindings } : {}),
                            },
                          })
                          .json<{ code: string; rationale?: string }>();
                      } catch (err) {
                        const e = err as { response?: Response };
                        if (e.response) {
                          try {
                            const body = (await e.response.json()) as {
                              message?: string | string[];
                            };
                            const msg = Array.isArray(body.message)
                              ? body.message.join('; ')
                              : body.message;
                            throw new Error(msg || `Generate failed (${e.response.status})`);
                          } catch (inner) {
                            if (inner instanceof Error && inner.message !== 'Generate failed')
                              throw inner;
                          }
                        }
                        throw err instanceof Error ? err : new Error(String(err));
                      }
                    }}
                  />
                </div>
              )}
            </div>

            <div className="pa-watch-step">
              <div className="pa-watch-step__head">
                <span className="pa-watch-step__num">3</span>
                <span className="pa-watch-step__title">How should the agent hear about it?</span>
              </div>
              <div className="pa-watch-mode-grid">
                {(
                  [
                    ['when-idle', 'Only if free (default)', 'No interrupt — skipped while busy'],
                    ['next-turn', 'After current work', 'Queues a turn — can feel spammy'],
                    ['inbox', 'Quiet update', 'Facts only — no forced turn'],
                  ] as const
                ).map(([id, label, hint]) => (
                  <button
                    key={id}
                    type="button"
                    className={
                      form.wakeDelivery === id ? 'pa-watch-mode pa-watch-mode--on' : 'pa-watch-mode'
                    }
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        wakeDelivery: id as PersistentAgentWatchDelivery,
                      }))
                    }
                  >
                    <span className="pa-watch-mode__label">{label}</span>
                    <span className="pa-watch-mode__hint">{hint}</span>
                  </button>
                ))}
              </div>
              <label className="field" style={{ marginTop: 10 }}>
                <span className="field-label">Message when it fires (optional)</span>
                <input
                  className="input"
                  value={form.wakePrompt}
                  placeholder="e.g. Battery low — check low_battery lane"
                  onChange={(e) => setForm((f) => ({ ...f, wakePrompt: e.target.value }))}
                />
              </label>
            </div>

            <div className="pa-watch-step">
              <div className="pa-watch-step__head">
                <span className="pa-watch-step__num">4</span>
                <span className="pa-watch-step__title">Timing</span>
              </div>
              <div className="pa-watch-sentence">
                <span>Check every</span>
                <input
                  className="input pa-watch-sentence__field"
                  type="number"
                  min={2}
                  max={3600}
                  value={form.intervalSec}
                  onChange={(e) => setForm((f) => ({ ...f, intervalSec: e.target.value }))}
                />
                <span>seconds</span>
                <span style={{ opacity: 0.5 }}>·</span>
                <span>run for</span>
                <input
                  className="input pa-watch-sentence__field"
                  type="number"
                  min={60}
                  placeholder="forever"
                  value={form.forSeconds}
                  onChange={(e) => setForm((f) => ({ ...f, forSeconds: e.target.value }))}
                  title="Empty = until stopped"
                />
                <span>seconds</span>
              </div>
              <div style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className="pa-watch-adv-link"
                  onClick={() => setAdvancedOpen((v) => !v)}
                >
                  {advancedOpen ? 'Hide advanced' : 'Fact lane & advanced…'}
                </button>
              </div>
              {advancedOpen && (
                <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <label className="field" style={{ flex: '1 1 160px' }}>
                    <span className="field-label">Fact lane</span>
                    <input
                      className="input t-mono"
                      value={form.laneId}
                      placeholder="auto from tool"
                      onChange={(e) =>
                        setForm((f) => ({ ...f, laneId: e.target.value, laneTouched: true }))
                      }
                    />
                  </label>
                  {wakeMode === 'any-change' && form.conditionOp && (
                    <label className="field" style={{ flex: '1 1 140px' }}>
                      <span className="field-label">Condition path (rare)</span>
                      <input
                        className="input t-mono"
                        value={form.conditionPath}
                        placeholder="usually empty"
                        onChange={(e) => setForm((f) => ({ ...f, conditionPath: e.target.value }))}
                      />
                    </label>
                  )}
                </div>
              )}
            </div>

            <div className="pa-watch-summary">
              <strong>Summary. </strong>
              {summaryLine}
            </div>

            <div className="pa-watch-actions">
              <button
                className="btn btn--primary btn--sm"
                disabled={
                  form.name.trim() === '' ||
                  form.name.trim().length > 64 ||
                  !form.mcpConnectionId ||
                  !form.toolName ||
                  form.laneId.trim() === '' ||
                  !intervalValid ||
                  !forSecondsValid ||
                  parsedArgs.error !== undefined ||
                  selectorInvalid ||
                  queryCompileError != null ||
                  (!form.queryEnabled &&
                    codeTrim !== '' &&
                    (parsedBindings.error !== undefined || parsedTimeout.error !== undefined)) ||
                  put.isPending ||
                  terminal
                }
                onClick={() => put.mutate()}
              >
                {put.isPending ? 'Saving…' : editingName !== null ? 'Save watch' : 'Start watching'}
              </button>
              <button
                className="btn btn--ghost btn--sm"
                disabled={put.isPending}
                onClick={() => {
                  put.reset();
                  preview.reset();
                  if (startInCreate && onSaved) onSaved();
                  else if (!startInCreate) setEditing(false);
                  else openWizard(null);
                }}
              >
                Cancel
              </button>
              {put.isError && (
                <span className="t-sm" style={{ color: 'var(--status-danger)' }}>
                  {String((put.error as Error)?.message ?? 'Save failed')}
                </span>
              )}
            </div>
            <p className="t-xs t-muted" style={{ margin: 0 }}>
              Unchanged polls cost nothing. The fact lane always holds the latest snapshot only.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

/** One watch in the list — status, cadence line, and row actions. */
function WatchRow({
  watch,
  pending,
  onToggle,
  onEdit,
  onDelete,
}: {
  watch: PersistentAgentWatch;
  pending: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const failing = watch.consecutiveErrors >= 20;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        padding: '6px 0',
        borderBottom: '1px solid var(--border-subtle)',
      }}
    >
      <span className="t-sm" style={{ fontWeight: 600 }}>
        {watch.name}
      </span>
      <span
        className={
          failing
            ? 'badge badge--danger'
            : watch.status === 'ACTIVE'
              ? 'badge badge--success'
              : 'badge badge--warning'
        }
        title={failing ? `${watch.consecutiveErrors} consecutive poll errors` : undefined}
      >
        {failing ? 'failing — paused' : watch.status === 'ACTIVE' ? 'active' : 'paused'}
      </span>
      {watch.code != null && watch.code !== '' && (
        <span
          className="badge badge--neutral"
          title="Custom JS transform decides what changed (instead of hash-on-change)"
        >
          JS
        </span>
      )}
      <span className="t-sm t-muted">
        {watch.toolName} · every {watch.intervalSec}s → lane <code>{watch.laneId}</code>
      </span>
      {watch.selector && <code className="t-xs">{watch.selector}</code>}
      <span className="t-xs t-muted" title={formatDateTime(watch.lastPolledAt)}>
        last poll {formatRelative(watch.lastPolledAt)}
      </span>
      <span style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
        <button
          className="btn btn--ghost btn--sm"
          disabled={pending}
          title={
            watch.status === 'ACTIVE'
              ? 'Keep the watch but stop polling until resumed'
              : 'Resume — polling restarts now (missed polls are not replayed)'
          }
          onClick={onToggle}
        >
          {watch.status === 'ACTIVE' ? 'Pause' : 'Resume'}
        </button>
        <button className="btn btn--ghost btn--sm" onClick={onEdit}>
          Edit
        </button>
        <button className="btn btn--destructive btn--sm" disabled={pending} onClick={onDelete}>
          Delete
        </button>
      </span>
    </div>
  );
}
